import { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { JobWithAlerts } from "../../models/JobWithAlerts";
import Table, { Column } from "../common/table";
import { XMarkIcon } from "@heroicons/react/24/outline";
// import { useSocket } from "./socket-context/SocketContext";
import JobsService from "../../services/job.service";
import SiteService from "../../services/site.service";
import { Site } from "../../types/Site";
import SimpleMap from "../common/SimpleMap";
import AlertService from "../../services/alert.service";
import { formatToShortCst } from "../../utils/formatToShortCst";
import { Alert } from "../../types/Alert";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Condition } from "../../types/Condition";

// Helper functions to manage dismissed jobs in localStorage
const DISMISSED_JOBS_KEY = 'fieldgistix_dismissed_jobs';

const getDismissedJobs = (): string[] => {
  try {
    const stored = localStorage.getItem(DISMISSED_JOBS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const addDismissedJob = (jobId: string): void => {
  const dismissed = getDismissedJobs();
  if (!dismissed.includes(jobId)) {
    dismissed.push(jobId);
    // Keep only the last 100 dismissed jobs to prevent localStorage from growing too large
    const trimmed = dismissed.slice(-100);
    localStorage.setItem(DISMISSED_JOBS_KEY, JSON.stringify(trimmed));
  }
};

const isJobDismissed = (jobId: string): boolean => {
  return getDismissedJobs().includes(jobId);
};

function findCondtionName(conditions: Condition[], conditionId: string) {
  const condition = conditions?.find((condition) => condition._id === conditionId);
  
  return condition?.name;
}

function classNames(...classes: any[]) {
  return classes.filter(Boolean).join(" ");
}

const alertTypeStyles = {
  MONITOR: "bg-orange-100 text-yellow-800",
  TELEMATIC_FAULT: "bg-orange-100 text-yellow-800",
  SYSTEM_NO_DATA: "bg-red-100 text-blue-800",
};

const statusStyles = {
  COMPLETED: "bg-green-100 text-green-800",
  PENDING: "bg-yellow-100 text-yellow-800",
  PENDING_MANAGER_APPROVAL: "bg-yellow-100 text-yellow-800",
  PENDING_OPERATOR_APPROVAL: "bg-yellow-100 text-yellow-800",
  ASSIGNED: "bg-blue-100 text-blue-800",
  UNASSIGNED: "bg-gray-100 text-red-800",
};

export default function ConfirmOperatorModal(props: {
  jobId?: string | null;
  onClose?: () => void;
}) {
  const [job, setJob] = useState<JobWithAlerts>();
  const [site, setSite] = useState<Site>();
  const [alerts, setAlerts] = useState<Alert[]>();
  const cancelButtonRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // const socket = useSocket();
  const { t } = useTranslation();
  const lastPolledJobIdRef = useRef<string | null>(null);

  const fetchJobById = async (jobId: string) => {
    if (jobId) {
      const jobResponse = await JobsService.getJobById(jobId);
      const data = await jobResponse.json();
      setJob(data);
      return data;
    }
    return null;
  };

  const fetchSiteData = async (siteId: string) => {
    if (siteId) {
      const siteResponse = await SiteService.getSite(siteId);
      const siteData = await siteResponse.json();
      setSite(siteData);
      return siteData;
    }
    return null;
  };

  const fetchAlerts = async (jobId: string) => {
    if (jobId) {
      const alertResponse = await AlertService.searchAlert({ jobId });
      const data = await alertResponse.json();
      setAlerts(data);
      return data;
    }
    return null;
  }

  const acceptOperator = async (jobId: string) => {
    const jobResponse = await JobsService.acceptJob(jobId);
    if (jobResponse.ok) {
      addDismissedJob(jobId); // Mark as handled so we don't show it again
      setIsOpen(false);
    }
  }

  const rejectOperator = async (jobId: string) => {
    const jobResponse = await JobsService.rejectJob(jobId);
    if (jobResponse.ok) {
      addDismissedJob(jobId); // Mark as dismissed so we don't show it again
      setIsOpen(false);
    }
  }

  const handlePendingManangerApproval = async (event: any) => {
    const jobId = event.jobId;
    console.log('Pending Manager Approval Event:', event);

    if (jobId) {
      const job = await fetchJobById(jobId);
      console.log(job);
      await fetchSiteData(job.siteId);
      await fetchAlerts(jobId);
      setIsOpen(true);
    }
  }

  const prettyAddress = () => {
    if (site) {
      return `${site.address.address1}, ${site.address.locality} - ${site.address.region} ${site.address.postalCode}, ${site.address.country}`;
    }
    return '';
  }
  

  // 02/01/2026 - ADD THIS BACK IN WHEN SOCKET ISSUES ARE RESOLVED
  // useEffect(() => {
  //   if (!socket) return;

  //   socket.on('user.status.pending-manager-approval', handlePendingManangerApproval);

  //   return () => {
  //     socket.off('user.status.pending-manager-approval');
  //   };
  // }, [socket]);

  // 02/01/2026 - Polling for latest job every 5 seconds (temporary replacement for websocket)
  useEffect(() => {
    const pollLatestJob = async () => {
      try {
        const response = await JobsService.getLatestJob();
        if (!response.ok) return;
        
        const latestJob = await response.json();
        
        if (!latestJob || !latestJob._id) {
          return;
        }

        // Skip if we've already shown this job or if it was dismissed
        if (latestJob._id === lastPolledJobIdRef.current) {
          return;
        }
        if (isJobDismissed(latestJob._id)) {
          return;
        }

        // Only show jobs that are pending manager approval
        if (latestJob.status !== 'PENDING_MANAGER_APPROVAL') {
          return;
        }

        lastPolledJobIdRef.current = latestJob._id;
        await handlePendingManangerApproval({ jobId: latestJob._id });
      } catch (error) {
        console.error('Error polling latest job:', error);
      }
    };

    const intervalId = setInterval(pollLatestJob, 5000);
    // Also poll immediately on mount
    pollLatestJob();

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => { }, [job, site, alerts]);

  useEffect(() => {
    const fetchData = async () => {
      const jobId = props.jobId;
      if (jobId) {
        const job = await fetchJobById(jobId);
        await fetchSiteData(job.siteId);
        await fetchAlerts(jobId);
        setIsOpen(true);
      }
    };
    fetchData();
  }, [props.jobId]);


  const alertTypeLabel = {
    MONITOR: t('sites.monitor'),
    SYSTEM_NO_DATA: t('sites.deviceDisconected'),
    TELEMATIC_FAULT: t('sites.telematicFault'),
  }

  const columns: Column<Alert>[] = [
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="hide-on-mobile bg-gray-50 px-6 py-2 text-left text-sm font-semibold text-gray-900"
          scope="col"
        >
          {t('sites.createdAt')}
        </th>
      ),
      renderRow: (row, _rowI, colI) => (
        <td
          key={colI}
          className="hide-on-mobile whitespace-nowrap px-6 py-2 text-left text-xs text-gray-500"
        >
          {formatToShortCst(row.createdAt || '')}
        </td>
      ),
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="bg-gray-50 px-6 py-2 text-left text-sm font-semibold text-gray-900"
          scope="col"
        >
        {t('sites.type')}
        </th>
      ),
      renderRow: (row, _rowI, colI) => (
        <td key={colI}
        className={classNames(
          // @ts-ignore
            row.triggerType && alertTypeStyles[row.triggerType],
            "inline-flex items-center px-2.5 py-0.5 rounded-full text-left text-xs font-medium capitalize"
        )}
    >
      {/* @ts-ignore */}
          {row.triggerType && alertTypeLabel[row.triggerType]}
        </td>
      ),
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="bg-gray-50 px-6 py-2 text-left text-sm font-semibold text-gray-900"
          scope="col"
        >
          {t('sites.monitor')}
        </th>
      ),
      renderRow: (row, _rowI, colI) => (
        <td key={colI} className="px-6 py-2 text-left text-xs text-gray-500">
          {row.monitor ? (
            <Link
              to={`/monitors/${row?.monitor?._id}`}
              className="text-blue-600 hover:underline"
            >
              {row.monitor?.name}
            </Link>
          ) : (
            '-'
          )}
        </td>
      ),
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="bg-gray-50 px-6 py-2 text-left text-sm font-semibold text-gray-900"
          scope="col"
        >
          {t('sites.condition')}
        </th>
      ),
      renderRow: (row, _rowI, colI) => (
        <td key={colI} className="px-6 py-2 text-left text-xs text-gray-500">
          {row.monitor && row.monitor?.conditions ? (
            <Link
              to={`/monitors/${row.monitor?._id}/conditions/${row.conditionId}`}
              className="text-blue-600 hover:underline"
            >
              {findCondtionName(row.monitor?.conditions, row.conditionId)}
            </Link>
          ) : (
            '-'
          )}
        </td>
      ),
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="bg-gray-50 px-6 py-2 text-left text-sm font-semibold text-gray-900"
          scope="col"
        >
          {t('sites.priority')}
        </th>
      ),
      renderRow: (row, _rowI, colI) => (
        <td key={colI} className="px-6 py-2 text-left text-xs text-gray-500">
          P{row.priority}
        </td>
      ),
    },
  ];

  return (
    <Transition.Root show={isOpen} as={Fragment}>
  <Dialog as="div" className="relative z-10" initialFocus={cancelButtonRef} onClose={() => { }}>
    <Transition.Child
      as={Fragment}
      enter="ease-out duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
    </Transition.Child>

    <div className="fixed inset-0 z-10 overflow-y-auto flex items-center justify-center p-4 sm:p-0">
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        enterTo="opacity-100 translate-y-0 sm:scale-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
      >
        <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white flex flex-col max-h-[80vh] w-full sm:max-w-7xl p-6">
          {/* Header */}
          <div className="flex justify-between items-center pb-4">
            <h5 className="text-2xl">Dispatch Operator</h5>
            <button
              type="button"
              className="rounded-md bg-red-600 p-1 text-white hover:bg-red-700 focus:outline-none"
              onClick={() => { 
                if (job?._id) addDismissedJob(job._id); // Mark as dismissed when closing
                props.onClose && props.onClose(); 
                setIsOpen(false); 
              }}
              ref={cancelButtonRef}
            >
              <XMarkIcon className="h-7 w-7" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-grow flex space-x-4">
            {/* Address and Map */}
            <div className="w-1/3">
              <p className="font-bold">{prettyAddress()}</p>
              {site && <SimpleMap coordinates={site?.location?.coordinates} width="100%" height="300px" />}
            </div>

            {/* Alerts Table */}
            <div className="w-2/3 max-h-[300px] overflow-y-auto">
              {isMobile ? (
                <div className="my-4 p-2 bg-yellow-200 rounded-lg">View on Desktop to see Alerts!</div>
              ) : (
                <Table
                  columns={columns}
                  rows={alerts || []}
                  title="Alerts"
                  currentPage={1}
                  pageSize={1}
                  totalRows={1}
                />
              )}
            </div>
          </div>

          {/* Action Section */}
          <div className="pt-4 border-t mt-4">
            <p>Would you like us to dispatch the nearest operator equipped with the necessary skills to address the alarm right away?</p>
            <div className="flex justify-end gap-4 mt-2">
              {job?._id && (
                <>
                  <button
                    onClick={() => acceptOperator(job._id)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md"
                  >
                    Accept
                  </button>
                  <button
                    onClick={() => rejectOperator(job._id)}
                    className="px-4 py-2 bg-red-600 text-white rounded-md"
                  >
                    Reject
                  </button>
                </>
              )}
            </div>
          </div>
        </Dialog.Panel>
      </Transition.Child>
    </div>
  </Dialog>
</Transition.Root>

  );
}
